<template>
  <!-- No tw- display classes here: Tailwind is !important and would beat v-show.
       The panel hides with v-show so the iframe, and any live call, survives. -->
  <div v-show="open" class="dialpad-phone elevation-8">
    <div class="dialpad-phone__header">
      <span class="tw-font-medium">Phone</span>

      <v-btn icon small aria-label="Hide phone" @click="$emit('close')">
        <v-icon small>close</v-icon>
      </v-btn>
    </div>

    <div v-if="ringing" class="dialpad-phone__ringing" role="alert">
      <TypeBody variant="text-small" weight="strong" tag="p">
        Incoming call: {{ ringing }}
      </TypeBody>
      <TypeBody variant="text-x-small" tag="p">
        Press Answer in the Dialpad window below.
      </TypeBody>
    </div>

    <div v-else-if="!tabEnabled" class="dialpad-phone__notice">
      <TypeBody variant="text-x-small" tag="p" class="tw-mb-2">
        Calls ring in one tab at a time. Sign in to Dialpad below, then turn
        this on to take calls here.
      </TypeBody>
      <BaseButton variant="secondary" size="small" block @click="enableTab">
        Take calls in this tab
      </BaseButton>
    </div>

    <iframe
      ref="frame"
      :src="url"
      :allow="allow"
      :sandbox="sandbox"
      title="Dialpad"
      class="dialpad-phone__frame"
    />
  </div>
</template>

<script>
import { BaseButton, TypeBody } from '@bambeehr/pollen';

import {
  CTI_ALLOW,
  CTI_ORIGIN,
  CTI_SANDBOX,
  CTI_URL,
  ctiMessage,
  parseCtiEvent,
  ringingCaller,
} from '@/lib/dialpadCti';

// BIO-2517: Dialpad's Mini Dialer, kept loaded in the layout so reps can answer
// inbound calls (like the office line) without the Dialpad app. Honey only opens
// the panel when a call rings; answering happens inside Dialpad's frame.
export default {
  name: 'DialpadPhone',

  components: {
    BaseButton,
    TypeBody,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      url: CTI_URL,
      allow: CTI_ALLOW,
      sandbox: CTI_SANDBOX,
      ringing: '',
      tabEnabled: false,
    };
  },

  mounted() {
    window.addEventListener('message', this.onMessage);
  },

  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
  },

  methods: {
    onMessage(event) {
      const message = parseCtiEvent(event);

      if (!message) {
        return;
      }

      if (message.method === 'call_ringing') {
        if (message.payload.state === 'on') {
          this.ringing = ringingCaller(message.payload);
          this.$emit('ringing');
        } else {
          this.ringing = '';
        }
      } else if (
        message.method === 'user_authentication' &&
        !message.payload.user_authenticated
      ) {
        this.tabEnabled = false;
      }
    },

    enableTab() {
      const frame = this.$refs.frame && this.$refs.frame.contentWindow;

      if (frame) {
        frame.postMessage(ctiMessage('enable_current_tab'), CTI_ORIGIN);
        this.tabEnabled = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dialpad-phone {
  background: #ffffff;
  border-radius: 8px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  height: 600px;
  max-height: calc(100vh - 80px);
  max-width: calc(100vw - 32px);
  position: fixed;
  right: 16px;
  width: 380px;
  z-index: 200;

  &__header {
    align-items: center;
    border-bottom: 1px solid #e8e8ec;
    display: flex;
    height: 48px;
    justify-content: space-between;
    padding: 0 12px;
  }

  &__ringing {
    background: #e8f6ee;
    padding: 12px;
  }

  &__notice {
    border-bottom: 1px solid #e8e8ec;
    padding: 12px;
  }

  &__frame {
    border: 0;
    flex: 1;
    width: 100%;
  }
}
</style>
